import axios from 'axios';
import { API_BASE_URL, NavigationPaths, TOKEN_KEY } from '../constants';
import { checkIfTokenIsExpired, jwtDecode } from '../utils';
import { NotificationProps } from '../types';
import i18n from 'i18next';
import { resetAuthState } from '../reducers/authReducer';
import { resetAdminState } from '../reducers/adminReducer';
import { resetOrganisationState } from '../reducers/organizationReducer';
import React from 'react';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

let interceptorId: number | null = null;
let interceptorEjected = false;

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      if (checkIfTokenIsExpired(jwtDecode(token).exp)) {
        localStorage.removeItem(TOKEN_KEY);
        window.location.href = '/login';
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

export const setupAxiosInterceptors = (
  addNotification: (data: Omit<NotificationProps, 'id'>) => void,
  dispatch: React.Dispatch<any>,
) => {
  if (interceptorId === null) {
    interceptorId = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error)) {
          const { response } = error;

          if (response && response.status >= 400 && response.status < 500) {
            if (response.status === 401) {
              addNotification({
                title: i18n.t('error'),
                message: i18n.t('unauthorized'),
                type: 'error',
              });
              localStorage.removeItem(TOKEN_KEY);
              dispatch(resetAuthState());
              dispatch(resetAdminState());
              dispatch(resetOrganisationState());
              return Promise.reject(error).then(
                () => (window.location.href = NavigationPaths.LOGIN),
              );
            }
            if (response.data.message) {
              addNotification({
                title: i18n.t('error'),
                message: i18n.t(response?.data?.message),
                type: 'error',
              });
            } else {
              addNotification({
                title: i18n.t('error'),
                message: i18n.t('clientErrorMessage'),
                type: 'error',
              });
            }
          }

          if (!response) {
            addNotification({
              title: i18n.t('error'),
              message: i18n.t('networkErrorMessage'),
              type: 'error',
            });
          } else if (response.status >= 500) {
            addNotification({
              title: i18n.t('error'),
              message: i18n.t('serverErrorMessage'),
              type: 'error',
            });
          }
        } else {
          addNotification({
            title: i18n.t('error'),
            message: i18n.t('unknownErrorMessage'),
            type: 'error',
          });
        }

        if (!interceptorEjected) {
          if (interceptorId !== null) {
            axiosInstance.interceptors.response.eject(interceptorId);
            interceptorId = null;
            interceptorEjected = true;
          }
        }

        return Promise.reject(error);
      },
    );
  }
};

export default axiosInstance;
